import { useEffect, useState, useCallback } from 'react';
import { Download, X, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Composant PWA
 *
 * Rôles :
 *   1. Enregistre le service-worker.js
 *   2. Capture l'évènement `beforeinstallprompt` (Android / Chrome / Edge)
 *      et affiche un bouton « Installer l'app »
 *   3. Détecte les mises à jour du SW et propose un rafraîchissement
 *
 * Affichage :
 *   - Bannière fixe en bas de l'écran, au-dessus de la bottom-nav mobile
 *   - Masquée après dismiss (clé localStorage, 7 jours)
 *   - Cachée si app déjà installée (mode standalone) ou pas de prompt dispo
 */

const DISMISS_KEY = 'sv_pwa_dismissed_at';
const DISMISS_DAYS = 7;

function isDismissed() {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const ageMs = Date.now() - Number(ts);
    return ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function markDismissed() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

export default function PWAInstaller() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  // ── Enregistrement du SW ────────────────────────────────────────────────
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    if (!('serviceWorker' in navigator)) return;

    let cancelled = false;
    let registration = null;
    let pollId = null;

    const register = async () => {
      try {
        const reg = await navigator.serviceWorker.register('/service-worker.js');
        registration = reg;

        // Nouveau SW déjà en attente
        if (reg.waiting && navigator.serviceWorker.controller) {
          if (!cancelled) {
            setWaitingWorker(reg.waiting);
            setUpdateReady(true);
          }
        }

        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Un nouveau SW est prêt mais en attente
              if (!cancelled) {
                setWaitingWorker(newWorker);
                setUpdateReady(true);
              }
            }
          });
        });

        // Vérifier les mises à jour : à l'ouverture, quand l'onglet redevient
        // visible, et périodiquement. Permet à la bannière d'apparaître sans
        // que l'utilisateur ait à recharger lui-même.
        const checkUpdate = () => { reg.update().catch(() => {}); };
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') checkUpdate();
        });
        pollId = setInterval(checkUpdate, 15 * 60 * 1000); // toutes les 15 min
      } catch (err) {
        console.warn('[PWA] Échec enregistrement SW :', err);
      }
    };

    register();

    // Recharger la page une seule fois quand le nouveau SW prend le contrôle
    // (déclenché par le clic « Mettre à jour » → SKIP_WAITING).
    let refreshing = false;
    const onCtrlChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onCtrlChange);

    return () => {
      cancelled = true;
      if (pollId) clearInterval(pollId);
      navigator.serviceWorker.removeEventListener('controllerchange', onCtrlChange);
    };
  }, []);

  // ── Capture beforeinstallprompt ─────────────────────────────────────────
  useEffect(() => {
    if (isStandalone()) return;
    if (isDismissed()) return;

    const handler = (e) => {
      e.preventDefault(); // garder le contrôle
      setDeferredPrompt(e);
      setVisible(true);
    };
    const installedHandler = () => {
      setVisible(false);
      setDeferredPrompt(null);
      toast.success('Application installée 🎉');
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  // ── Actions utilisateur ────────────────────────────────────────────────
  const onInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        setVisible(false);
      }
      setDeferredPrompt(null);
    } catch {
      setDeferredPrompt(null);
      setVisible(false);
    }
  }, [deferredPrompt]);

  const onDismiss = useCallback(() => {
    markDismissed();
    setVisible(false);
  }, []);

  const onUpdate = useCallback(() => {
    if (!waitingWorker) return;
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    // Le `controllerchange` ci-dessus déclenchera le reload
    setUpdateReady(false);
  }, [waitingWorker]);

  // ── Rendu ──────────────────────────────────────────────────────────────
  // Priorité à la mise à jour si dispo (plus important que l'install)
  if (updateReady) {
    return (
      <div
        className="fixed left-3 right-3 z-[60] flex items-center gap-3 px-4 py-3 bg-gray-900 border border-yellow-600/60 rounded-xl shadow-2xl"
        style={{
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)',
        }}
        role="status"
      >
        <RefreshCw size={20} className="text-yellow-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">Nouvelle version disponible</p>
          <p className="text-xs text-gray-400">Recharge pour mettre à jour l'application.</p>
        </div>
        <button
          onClick={onUpdate}
          className="px-3 py-2 text-sm font-semibold bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 min-h-[40px]"
        >
          Mettre à jour
        </button>
      </div>
    );
  }

  if (!visible || !deferredPrompt) return null;

  return (
    <div
      className="fixed left-3 right-3 z-[60] flex items-center gap-3 px-4 py-3 bg-gray-900 border border-yellow-600/60 rounded-xl shadow-2xl"
      style={{
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)',
      }}
      role="dialog"
      aria-label="Installer l'application"
    >
      <Download size={20} className="text-yellow-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white">Installer l'application</p>
        <p className="text-xs text-gray-400 truncate">Accès en un clic depuis l'écran d'accueil.</p>
      </div>
      <button
        onClick={onInstall}
        className="px-3 py-2 text-sm font-semibold bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 min-h-[40px]"
      >
        Installer
      </button>
      <button
        onClick={onDismiss}
        aria-label="Fermer"
        className="p-2 text-gray-400 hover:text-white min-h-[40px] min-w-[40px] flex items-center justify-center"
      >
        <X size={18} />
      </button>
    </div>
  );
}
